import cx from 'classnames';
import type { CSSProperties, FC, PropsWithChildren, ReactNode } from 'react';
import { useRef } from 'react';

import type { Nullable } from '@zen/utils/typescript';

import type { RowSpan, TableColumn } from '../../types';
import { onCellMouseEnter, onCellMouseLeave } from './helpers';

export interface Props {
  className?: string;
  columnConfiguration?: Pick<TableColumn, 'alignment' | 'ellipsis' | 'key' | 'width'> & {
    isPartOfMergeByColumn: boolean;
    rowSpans?: Array<RowSpan>;
  };
  fixed?: 'left' | 'right';
  fixedOffset?: number;
  isFirstCell?: boolean;
  isLastCell?: boolean;
  isNested?: boolean;
  isSelected?: boolean;
  rowIndex: number;
  rowSpan?: number;
  style?: CSSProperties;
  tableId: string;
}

const TableCell: FC<PropsWithChildren<Props>> = (props) => {
  const {
    children,
    className,
    columnConfiguration,
    fixed,
    fixedOffset = 0,
    isFirstCell,
    isLastCell,
    isNested = false,
    isSelected = false,
    rowIndex,
    rowSpan: rowSpanProp,
    style,
    tableId,
    ...rest
  } = props;
  const cellElement = useRef<HTMLTableCellElement>(null);

  const { alignment = 'left', ellipsis, key, width, rowSpans } = columnConfiguration || {};

  const colKey: Nullable<string> = key ? `col-${key}` : null;
  const cellStyle: CSSProperties = {
    ...style,
    minWidth: width,
    ...(ellipsis && width !== undefined && { maxWidth: width }),
    ...(fixed && {
      position: 'sticky',
      [fixed]: fixedOffset,
      zIndex: 10,
      width: '1%'
    })
  };

  // Use the explicit rowSpan prop if provided, otherwise calculate from rowSpans config
  const rowSpanFromConfig: RowSpan | undefined = rowSpans?.find(({ atIndex }) => atIndex === rowIndex);
  const rowSpanValue: number | undefined = rowSpanProp ?? rowSpanFromConfig?.value;

  // Nested/selected rows have different background, regular cells use white (hover handled by JS)
  const backgroundClass = isNested || isSelected ? 'bg-azure-lightest' : 'bg-white';

  const isUtilityColumn = key === 'expandColumn' || key === 'bulkSelection';

  const classNames: string = cx(
    {
      'border-solid border-r-grey-lighter border-r': columnConfiguration?.isPartOfMergeByColumn,
      'merge-column-cell': columnConfiguration?.isPartOfMergeByColumn,
      'pl-6': isFirstCell,
      'pr-6': isLastCell,
      '!pr-0': isUtilityColumn,
      'relative before:absolute before:left-0 before:-top-px before:-bottom-px before:w-px before:bg-azure-base before:z-10':
        isNested && isFirstCell,
      'shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)] zen-table-cell-fix-left': fixed === 'left',
      'shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.1)] zen-table-cell-fix-right': fixed === 'right'
    },
    'group/cell h-12 px-4 py-1 text-sm text-grey-dark whitespace-nowrap align-middle border-b border-grey-lighter',
    backgroundClass,
    className
  );

  const cellContentClassName: string = cx('flex h-full min-w-0', {
    'items-start pt-2': alignment === 'top-start',
    'justify-end items-center': alignment === 'right',
    'items-center': alignment === 'left'
  });

  const renderContent = (): ReactNode => {
    if (ellipsis) {
      return <span className="truncate">{children}</span>;
    }

    return children;
  };

  const handleMouseEnter = (): void => {
    onCellMouseEnter({ element: cellElement.current, rowIndex, tableId });
  };

  const handleMouseLeave = (): void => {
    onCellMouseLeave({ tableId });
  };

  return (
    <td
      {...rest}
      ref={cellElement}
      className={classNames}
      data-component="table-cell"
      data-testid={colKey}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      rowSpan={rowSpanValue}
      style={cellStyle}
    >
      <div className={cellContentClassName}>{renderContent()}</div>
    </td>
  );
};

export type { Props as TableCellProps };

export default TableCell;
